import { subscribe, unstable_buildProxyFunction } from "valtio";

import { IS_DEV, IS_NODE_BROWSERLESS } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import deepEqual from "@/util/deep-equal.mjs";
import { devError } from "@/util/dev.mjs";
import sourceMap from "@/util/error-sourcemap.mjs";
import globals from "@/util/global-whitelist.mjs";

export { useSnapshot } from "valtio";

function proxyDeepEqual(a: unknown, b: unknown) {
  if (typeof a === "object" && typeof b === "object") {
    if (refSet.has(a) || refSet.has(b)) {
      return a === b;
    }
  }

  return deepEqual(a, b);
}

const [innerProxy, proxyStateMap, refSet] =
  unstable_buildProxyFunction(proxyDeepEqual);

export function isValtioProxy(obj: object) {
  return proxyStateMap.has(obj);
}

/**
 * Creates a `valtio` proxy that uses `deepEqual` instead of `Object.is` for
 * equality checks as well as storing the proxy in `__BLITZ_DEV__` for debugging.
 *
 * @param initialObject
 * @returns
 */
export function proxy<T extends object>(initialObject: T): T {
  const proxiedObject = innerProxy(initialObject);

  if (IS_DEV) {
    subscribe(proxiedObject, (ops) => {
      ops.forEach((op) => {
        if (op[0] === "set") {
          const [, path, value] = op;

          if (value && typeof value === "object") {
            sanityCheckObject(path, value);
          }
        }
      });
    });
  }

  if (IS_DEV && !IS_NODE_BROWSERLESS) {
    sourceMap(new Error()).then(([_out, output]) => {
      if (output.length < 2) return;

      const [_, { fileName }] = output;

      globals.__BLITZ_DEV__.proxies ??= {};
      globals.__BLITZ_DEV__.proxies[fileName] ??= [];
      globals.__BLITZ_DEV__.proxies[fileName].push(proxiedObject);
    });
  }

  return proxiedObject;
}

/**
 * Some types of objects or classes are not safe to store in a Proxy, so we want to
 * ensure that they are wrapped in a `ref()` before being proxied.
 */
function sanityCheckObject(path: (string | symbol)[], obj: object) {
  if (!obj || typeof obj !== "object") return;

  // These are generally safe. Feel free to add more if you're sure.
  if (
    obj instanceof Error ||
    obj instanceof Promise ||
    obj instanceof Date ||
    Array.isArray(obj)
  ) {
    return;
  }

  if (refSet.has(obj)) {
    return;
  }

  mainRefs.enableAccessWarnings = false;

  if (obj["$$typeof"] === Symbol.for("react.element")) {
    devError(
      "Do not put react elements in a proxy! These should be wrapped in ref() if absolutely necessary!",
      { path, value: obj },
    );
  } else if (!obj.constructor?.toString().match(/Object/)) {
    devError(
      `Do not put ${obj.constructor?.name} in a proxy unless they're wrapped in ref() first!`,
      { path, value: obj },
    );
  }

  mainRefs.enableAccessWarnings = true;

  for (const key in obj) {
    const value = obj[key];

    if (value && typeof value === "object") {
      sanityCheckObject([...path, key], value);
    }
  }
}
